<script setup lang="ts">
const props = defineProps<{
  data: FactoryProps
  template?: 'page' | 'overlay'
}>()

const modalContent = ref('')
const modalTitle = ref('')

const closeModal = () => {
  const dialog = document.getElementById('dialog') as HTMLDialogElement
  if (dialog) {
    dialog.close()
  }
}

const openModal = (factory: { name: string; description?: string }) => {
  modalTitle.value = factory.name
  modalContent.value = factory.description || ''

  // Open the dialog using the command API
  const dialog = document.getElementById('dialog') as HTMLDialogElement
  if (dialog) {
    dialog.showModal()
  }
}

const handleDialogClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (target.tagName === 'DIALOG') {
    closeModal()
  }
}
</script>

<template>
  <section
    class="px-bleed"
    :class="{
      'bg-white':
        template !== 'page' && template !== 'overlay' && props.data.theme !== 'light-grey',
      'bg-light-grey':
        template === 'page' || (template !== 'overlay' && props.data.theme === 'light-grey'),
    }"
  >
    <ul
      class="gap-gap grid grid-cols-1 gap-y-10 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
    >
      <li v-for="(factory, idx) in data.factory || []" :key="idx" class="group factories-item">
        <div class="group relative shrink-0 snap-center overflow-hidden object-cover">
          <CommonMedia
            :sizes="['33vw', '50vw']"
            :aspect="data.crop ? 5 / 4 : undefined"
            :fill="'w'"
            class="mb-0 max-h-[280px] object-cover"
            :media="factory.media"
          />
          <div
            class="content bg-neon-yellow ease-out-quint absolute right-0 bottom-0 flex w-[80%] flex-col p-4 transition-all duration-500 max-lg:w-[66%] xl:w-1/2"
          >
            <!-- just a visual representation of a link -->
            <ElementsLogomarkLink
              v-for="(link, linkIdx) in factory.callToAction"
              :key="linkIdx"
              :link="link"
              :dummy="true"
              class="mt-auto"
            />
          </div>
          <!-- the actual link (invisible) -->
          <ElementsLogomarkLink
            v-for="(link, linkIdx2) in factory.callToAction"
            :key="linkIdx2"
            :link="link"
            :make-container-clickable="true"
            :visible="false"
          />
        </div>
        <div class="mt-4 flex flex-col gap-1">
          <p class="font-display mb-3 text-xl uppercase font-stretch-expanded">
            {{ factory.name }}
          </p>
          <span>{{ factory.description?.slice(0, 99) }}...</span>
          <div
            v-if="factory.description?.length && factory.description?.length > 100"
            class="more-button mt-2 w-fit"
            @click="openModal(factory)"
          >
            <!-- just a visual representation of a link -->
            <ElementsLogomarkLink
              v-for="(link, linkIdx) in [{ title: 'Read more', target: '_self' }]"
              :key="linkIdx"
              :link="{ title: 'Read more', target: '_self' }"
              :dummy="true"
              class="mt-auto cursor-pointer"
            />
          </div>
          <!-- the actual link (invisible) -->
          <ElementsLogomarkLink
            v-for="(link, linkIdx2) in [{ title: 'Read more', target: '_self' }]"
            :key="linkIdx2"
            :link="{ title: 'Read more', target: '_self' }"
            :make-container-clickable="true"
            :visible="false"
          />
        </div>
      </li>
    </ul>
  </section>

  <el-dialog>
    <dialog
      id="dialog"
      aria-labelledby="dialog-title"
      class="fixed inset-0 size-auto max-h-none max-w-none overflow-y-auto bg-transparent backdrop:bg-transparent"
      @click="handleDialogClick"
    >
      <el-dialog-backdrop
        class="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
        @click="closeModal"
      />

      <div
        tabindex="0"
        class="flex min-h-full items-center justify-center p-2 text-center focus:outline-none sm:p-4 md:p-6"
        @click="closeModal"
      >
        <el-dialog-panel
          class="transition- relative w-full max-w-sm transform overflow-hidden bg-white text-left shadow-xl data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:max-w-md data-closed:sm:translate-y-0 data-closed:sm:scale-95 md:max-w-lg lg:max-w-xl xl:max-w-2xl"
          @click.stop
        >
          <div class="bg-neon-yellow p-4 sm:p-6 md:p-8">
            <div class="sm:flex sm:items-start">
              <div class="text-center sm:mt-0 sm:text-left">
                <h3 id="dialog-title" class="flex items-start justify-between gap-3">
                  <p
                    class="font-display text-lg leading-tight uppercase font-stretch-expanded sm:text-xl md:text-2xl"
                  >
                    {{ modalTitle }}
                  </p>
                  <button
                    type="button"
                    class="flex-shrink-0 cursor-pointer p-1"
                    command="close"
                    commandfor="dialog"
                  >
                    <IconsCross class="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                </h3>
                <div class="mt-4 sm:mt-6">
                  <div
                    v-if="modalContent && typeof modalContent === 'object'"
                    class="prose prose-sm sm:prose-base max-w-none"
                  >
                    <ElementsPortableTextWrapper :content="modalContent" />
                  </div>

                  <div
                    v-else-if="modalContent && typeof modalContent === 'string'"
                    class="text-sm sm:text-base"
                  >
                    <p class="leading-relaxed">{{ modalContent }}</p>
                  </div>

                  <div v-else class="text-sm text-gray-500 italic sm:text-base">
                    No description available for this factory.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </el-dialog-panel>
      </div>
    </dialog>
  </el-dialog>
</template>
<style lang="postcss">
.factories-item {
  @media (width >= 20rem) {
    @media (hover: hover) {
      .content {
        transform: translateX(20px);
        opacity: 0;
      }

      &:has(:hover) {
        .content {
          transform: translateX(0px);
          opacity: 1;
        }
      }
    }
  }

  /* Show more button on hover */
  @media (hover: hover) {
    .more-button {
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover .more-button {
      opacity: 1;
    }
  }

  /* Always show on mobile/touch devices */
  @media (hover: none) {
    .more-button {
      opacity: 1 !important;
    }
  }
}
</style>
